import React, { useState, useRef } from 'react';
import { Paperclip, X, Upload, AlertTriangle, CheckCircle, File, Image, Video, Music } from 'lucide-react';
import FileService, { FileMetadata, UploadProgress, FileValidationResult } from '../../services/FileService';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';

interface FileAttachmentProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxTotalSize?: number;
  allowedTypes?: string[];
  category?: FileMetadata['category'];
  disabled?: boolean;
  showPreview?: boolean;
  className?: string;
}

export function FileAttachment({
  files,
  onFilesChange,
  maxFiles = 10,
  maxTotalSize = 50 * 1024 * 1024, // 50MB
  allowedTypes,
  category = 'other',
  disabled = false,
  showPreview = true,
  className = ''
}: FileAttachmentProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationResult, setValidationResult] = useState<FileValidationResult | null>(null);

  const handleFileSelect = (selectedFiles: FileList | File[]) => {
    const fileArray = Array.from(selectedFiles);
    const newFiles = [...files, ...fileArray];

    // Limit number of files
    if (newFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate files
    const validation = FileService.validateFiles(newFiles);
    setValidationResult(validation);

    if (validation.errors.length > 0) {
      toast.error(validation.errors[0]);
      return;
    }

    if (validation.warnings.length > 0) {
      validation.warnings.forEach(warning => toast.warning(warning));
    }

    onFilesChange(newFiles);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFileSelect(droppedFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
    
    // Re-validate remaining files
    if (newFiles.length > 0) {
      const validation = FileService.validateFiles(newFiles);
      setValidationResult(validation);
    } else {
      setValidationResult(null);
    }
  };

  const getFileIcon = (file: File) => {
    if (FileService.isImage(file.type)) return <Image className="w-4 h-4" />;
    if (FileService.isVideo(file.type)) return <Video className="w-4 h-4" />;
    if (FileService.isAudio(file.type)) return <Music className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Drop Zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-4 text-center transition-colors
          ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-600">
          Drop files here or <span className="text-blue-600 font-medium">click to browse</span>
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Max {maxFiles} files, {FileService.formatFileSize(maxTotalSize)} total
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
        accept={allowedTypes?.join(',') || 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt'}
        disabled={disabled}
      />

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              Attached Files ({files.length})
            </p>
            <p className="text-xs text-gray-500">
              {FileService.formatFileSize(totalSize)}
            </p>
          </div>

          <div className="space-y-1 max-h-40 overflow-y-auto">
            {files.map((file, index) => (
              <FileItem
                key={index}
                file={file}
                onRemove={() => removeFile(index)}
                showPreview={showPreview}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}

      {/* Validation Messages */}
      {validationResult && (
        <div className="space-y-1">
          {validationResult.errors.map((error, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          ))}
          {validationResult.warnings.map((warning, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-yellow-600">
              <AlertTriangle className="w-4 h-4" />
              {warning}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface FileItemProps {
  file: File;
  onRemove: () => void;
  showPreview: boolean;
  disabled: boolean;
}

function FileItem({ file, onRemove, showPreview, disabled }: FileItemProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  React.useEffect(() => {
    if (showPreview && FileService.isImage(file.type)) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file, showPreview]);

  const getFileIcon = () => {
    if (FileService.isImage(file.type)) return <Image className="w-4 h-4 text-blue-500" />;
    if (FileService.isVideo(file.type)) return <Video className="w-4 h-4 text-purple-500" />;
    if (FileService.isAudio(file.type)) return <Music className="w-4 h-4 text-green-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg group">
      {/* Preview or Icon */}
      <div className="flex-shrink-0">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name}
            className="w-10 h-10 object-cover rounded border"
          />
        ) : (
          <div className="w-10 h-10 flex items-center justify-center bg-white rounded border">
            {getFileIcon()}
          </div>
        )}
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {file.name}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{FileService.formatFileSize(file.size)}</span>
          <span>•</span>
          <span>{file.type}</span>
        </div>
      </div>

      {/* Remove Button */}
      <button
        onClick={onRemove}
        disabled={disabled}
        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default FileAttachment;